
import { PackageTier, PackageOption, packageTierIconMap, OrganizationCurrency } from 'fourwaves-shared';
import { Vue, Component, Prop, Model } from 'nuxt-property-decorator';
import { InputRadio } from 'fourwaves-shared/components';
import MoneyInput from '~/components/form/MoneyInput.vue';
import { featureListByPackageTier, PackageFeature, isFeatureFees } from '~/services/featureMapping';

@Component({
  components: {
    InputRadio,
    MoneyInput,
  },
})
export default class CheckoutPackageSelect extends Vue {
  @Prop({ type: Object, required: true }) readonly options!: Record<string, PackageOption>;
  @Prop({ type: String, default: 'USD' }) readonly currency!: OrganizationCurrency;
  @Prop(Boolean) readonly customPlanMode!: boolean;

  @Model('change', { type: String, default: null }) readonly value!: string;

  readonly PackageTier = PackageTier;

  public getOptionModifier(option: PackageOption) {
    return {
      '-checked': this.value === option.id,
      '-disabled': option.disabled,
    };
  }

  public getFormattedCost(option: PackageOption) {
    const symbol = this.currency === 'EUR' ? '€' : '$';
    return `${symbol}${option.cost.toLocaleString()}`;
  }

  public getNormalizedFee(fee: number) {
    // toFixed is used to avoid floating point js calculation errors
    return Number((fee * 100).toFixed(2));
  }

  public getDisableReason(option: PackageOption) {
    return option.current ? this.$t('checkout.packages.current') : this.$t('checkout.packages.downgrade');
  }

  public getCtaLabel(optionId: string) {
    return this.value === optionId ? this.$t('checkout.packages.selected') : this.$t('checkout.packages.select');
  }

  public getFeatureList(tier: PackageTier): PackageFeature[] {
    return featureListByPackageTier[tier].filter(({ key }) => !isFeatureFees(key));
  }

  public onOptionChange(optionId: string, key: string, value: number) {
    const updatedOption = { ...this.options[optionId], [key]: value };
    this.$emit('update:options', { ...this.options, [optionId]: updatedOption });
  }

  public onFeeChange(optionId: string, value: number) {
    const formattedValue = Number((value / 100).toFixed(4));
    const updatedOption = { ...this.options[optionId], fee: formattedValue };
    this.$emit('update:options', { ...this.options, [optionId]: updatedOption });
  }

  public onPackageClick(optionId: string) {
    if (this.value === optionId) this.$emit('change', '');
    else this.$emit('change', optionId);
  }

  public getIconName(tier: PackageTier) {
    return packageTierIconMap[tier];
  }
}
